import { useCallback } from 'react';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import type { SettingsSectionId } from '~/lib/settings-sections';
import type { SystemSettingsSectionId } from '~/lib/system-settings-sections';
import { useNavigate } from 'react-router';
import { settingsPath } from '~/lib/settings-sections';
import { systemSettingsPath } from '~/lib/system-settings-sections';

/**
 * FRONT-1938: the one choke point every "Setup" trigger goes through —
 * mirrors `startNewChat`/`openExistingChat` in `app-header.tsx`, which route
 * every "open chat" trigger through `useChatOpenStore` for a similar reason:
 * a chat open-request QUEUED but not yet consumed has to be cleared, or it
 * reopens a stale chat well after Settings closed, with no visible connection
 * to this click.
 *
 * Settings is the `/settings` route modal (`routes/settings.tsx`).
 */
export function useOpenSettings(): (section?: SettingsSectionId) => void {
  const clearOpenChat = useChatOpenStore((s) => s.clearOpenChat);
  const navigate = useNavigate();
  return useCallback(
    (section) => {
      clearOpenChat();
      navigate(section ? settingsPath(section) : '/settings');
    },
    [clearOpenChat, navigate],
  );
}

/** FRONT-2376: the System Settings counterpart to {@link useOpenSettings} — the `/system-settings` route modal. */
export function useOpenSystemSettings(): (section?: SystemSettingsSectionId) => void {
  const clearOpenChat = useChatOpenStore((s) => s.clearOpenChat);
  const navigate = useNavigate();
  return useCallback(
    (section) => {
      clearOpenChat();
      navigate(section ? systemSettingsPath(section) : '/system-settings');
    },
    [clearOpenChat, navigate],
  );
}
